<template>
  <v-card class="custom-calendar" elevation="0">
    <v-card-title class="d-flex justify-space-between align-center pa-0 px-1 mb-4">
      <v-btn icon small @click="previousMonth">
        <v-icon>$chevronLeftIcon</v-icon>
      </v-btn>

      <div class="d-flex align-center" style="cursor:pointer" @click="toggleMonthYearPicker">
        <v-btn text class="fonts-s16 fonts-family-helveticaNeue h-100 ">{{ currentMonthYear }}</v-btn>
      </div>

      <v-btn icon small @click="nextMonth">
        <v-icon>$chevronRightIcon</v-icon>
      </v-btn>
    </v-card-title>

    

    <v-card-text class="py-0 px-1">

      <!-- Selector de Mes y Año -->
      <div v-if="showMonthYearPicker" class="month-year-picker pa-2">
        <v-select
          v-model="selectedYear"
          :items="yearsRange"
          label="Select Year"
          class="select-search max-w-270"
          solo
          elevation="0"
          dense
          hide-details
          append-icon="$caretUp"
        ></v-select>

        <div class="months-grid">
          <v-btn
            v-for="(m, index) in months"
            :key="index"
            small
            class="month-btn"
            :color="index === currentDate.getMonth() && selectedYear === currentDate.getFullYear() ? 'custom-btn-round-greenDark' : 'custom-btn-round-greenLight'"
            @click="setMonth(index)"
          >
            {{ m }}
          </v-btn>
        </div>
      </div>

      <div v-else>
        <div class="d-flex align-center mb-4">
          <v-text-field
          
            :value="formattedStartDate"
            rounded
            dense
            solo
            readonly
            hide-details
            class="date-input"
          ></v-text-field>
          <span class="mx-2">–</span>
          <v-text-field
          
            :value="formattedEndDate"
            rounded
            dense
            solo
            readonly
            hide-details
            class="date-input"
          ></v-text-field>
        </div>

        <div class="d-flex justify-space-between mb-4 w-100">
          <v-btn
            v-for="action in quickActions"
            :key="action.label"
            text
            small
            class="fonts-s14 fonts-500 fonts-family-helveticaNeue px-2 py-1"
            color="greenDark"
            @click="applyQuickAction(action)"
          >
            {{ action.label }}
          </v-btn>
        </div>

        <div class="weekdays-grid">
          <div
            v-for="day in weekDays"
            :key="day"
            class="gray-item text-center fonts-s14 fonts-500 fonts-family-helveticaNeue"
          >
            {{ day }}
          </div>
        </div>

        <div class="calendar-grid">
          <v-btn
            v-for="day in calendarDays"
            class="pa-0"
            :key="day.key"
            :class="getDayClass(day)"
            :disabled="day.disabled"
            text
            small
            :ripple="false"
            @click="selectDate(day)"
          >
            {{ day.date }}
          </v-btn>
        </div>
      </div>

    </v-card-text>

    <v-card-actions class="py-4 px-0 content-actions">
      <div class="d-flex flex-grow-1 gp-12">
        <v-btn solo class="flex-grow-1 custom-btn-round-greenLight custom-btn-round-size40"  @click="cancel">
          Cancel
        </v-btn>
        <v-btn solo class="flex-grow-1 custom-btn-round-greenDark custom-btn-round-size40" @click="apply">
          Apply
        </v-btn>
      </div>
    </v-card-actions>
  </v-card>
</template>

<script>
export default {
  name: "CustomCalendar",
  props: {
    value: {
      type: Object,
      default: () => ({ start: null, end: null }),
    },
    quickActions: {
      type: Array,
      default: () => [
        { label: "Last week", days: 7 },
        { label: "Last month", days: 30 },
        { label: "Last year", days: 365 },
      ],
    },
    weekDays: {
      type: Array,
      default: () => ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"],
    },
  },
  data() {
    return {
      currentDate: new Date(),
      startDate: null,
      endDate: null,
      hoverDate: null,
      showMonthYearPicker: false,
      selectedYear: new Date().getFullYear(),
    };
  },
  computed: {
    currentMonthYear() {
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      return `${months[this.currentDate.getMonth()]} ${this.currentDate.getFullYear()}`;
    },
    formattedStartDate() {
      return this.startDate ? this.formatDate(this.startDate) : "";
    },
    formattedEndDate() {
      return this.endDate ? this.formatDate(this.endDate) : "";
    },
    calendarDays() {
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();

      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);

      let firstDayOfWeek = firstDay.getDay();
      firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

      const days = [];
      const prevMonthLastDay = new Date(year, month, 0).getDate();
      for (let i = firstDayOfWeek - 1; i >= 0; i--) {
        days.push({ date: prevMonthLastDay - i, month: "prev", disabled: true, key: `prev-${prevMonthLastDay - i}` });
      }

      for (let i = 1; i <= lastDay.getDate(); i++) {
        days.push({ date: i, month: "current", disabled: false, fullDate: new Date(year, month, i), key: `current-${i}` });
      }

      const remainingDays = 42 - days.length;
      for (let i = 1; i <= remainingDays; i++) {
        days.push({ date: i, month: "next", disabled: true, key: `next-${i}` });
      }

      return days;
    },
    months() {
      return ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    },
    yearsRange() {
      const currentYear = new Date().getFullYear();
      const range = [];
      for (let y = 1999; y <= currentYear + 10; y++) range.push(y);
      return range;
    }
  },
  watch: {
    value: {
      handler(val) {
        if (!val) return;
        const newStart = val.start ? new Date(val.start) : null;
        const newEnd = val.end ? new Date(val.end) : null;
        if (this.startDate?.getTime() !== newStart?.getTime() || this.endDate?.getTime() !== newEnd?.getTime()) {
          this.startDate = newStart;
          this.endDate = newEnd;
        }
      },
      immediate: true,
      deep: true,
    }
  },
  methods: {
    previousMonth() {
      this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - 1, 1);
    },
    nextMonth() {
      this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 1);
    },
    toggleMonthYearPicker() {
      this.showMonthYearPicker = !this.showMonthYearPicker;
    },
    setMonth(monthIndex) {
      this.currentDate = new Date(this.selectedYear, monthIndex, 1);
      this.showMonthYearPicker = false;
    },
    selectDate(day) {
      if (day.disabled) return;
      const selectedDate = day.fullDate;
      if (!this.startDate || (this.startDate && this.endDate)) {
        this.startDate = selectedDate;
        this.endDate = null;
      } else {
        if (selectedDate < this.startDate) {
          this.endDate = this.startDate;
          this.startDate = selectedDate;
        } else {
          this.endDate = selectedDate;
        }
      }
    },
    getDayClass(day) {
      if (day.disabled) return "calendar-day disabled-day";
      const classes = ["calendar-day"];
      if (this.startDate && this.isSameDay(day.fullDate, this.startDate)) classes.push("selected-start");
      if (this.endDate && this.isSameDay(day.fullDate, this.endDate)) classes.push("selected-end");
      if (this.startDate && this.endDate && day.fullDate > this.startDate && day.fullDate < this.endDate) classes.push("in-range");
      return classes.join(" ");
    },
    isSameDay(date1, date2) {
      if (!date1 || !date2) return false;
      return date1.toDateString() === date2.toDateString();
    },
    formatDate(date) {
      const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    },
    applyQuickAction(action) {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - action.days);
      this.startDate = start;
      this.endDate = end;
    },
    cancel() {
      this.startDate = this.value.start ? new Date(this.value.start) : null;
      this.endDate = this.value.end ? new Date(this.value.end) : null;
      this.$emit("cancel");
    },
    apply() {
      this.$emit("input", { start: this.startDate, end: this.endDate });
      this.$emit("apply", { start: this.startDate, end: this.endDate });
    }
  },
};
</script>
